import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import "./BirthDatePickerModal.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const padDatePart = (value) => String(value).padStart(2, "0");
const WHEEL_ITEM_HEIGHT = 42;

function WheelColumn({ label, ariaLabel, options, value, onChange, columnRef }) {
  const scrollRef = useRef(null);
  const scrollTimerRef = useRef(null);
  const programmaticScrollRef = useRef(false);
  const programmaticTargetRef = useRef(null);
  const manualScrollRef = useRef(false);
  const selectedIndex = Math.max(0, options.findIndex((option) => String(option.value) === String(value)));

  const setRefs = (node) => {
    scrollRef.current = node;
    if (columnRef) columnRef.current = node;
  };

  const scrollToIndex = (index, behavior = "smooth") => {
    const node = scrollRef.current;
    if (!node) return;
    const top = Math.max(0, Math.min(options.length - 1, index)) * WHEEL_ITEM_HEIGHT;
    if (typeof node.scrollTo === "function") node.scrollTo({ top, behavior });
    else node.scrollTop = top;
  };

  useEffect(() => {
    if (manualScrollRef.current) {
      manualScrollRef.current = false;
      return undefined;
    }
    if (programmaticScrollRef.current) return undefined;
    const frame = window.requestAnimationFrame(() => scrollToIndex(selectedIndex, "auto"));
    return () => window.cancelAnimationFrame(frame);
    // Scrolling the wheel must not also force it to an exact row mid-gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, options.length]);

  useEffect(() => () => window.clearTimeout(scrollTimerRef.current), []);

  const selectIndex = (index, behavior = "smooth") => {
    const option = options[Math.max(0, Math.min(options.length - 1, index))];
    if (!option) return;
    if (String(option.value) === String(value) && Math.abs((scrollRef.current?.scrollTop ?? 0) - index * WHEEL_ITEM_HEIGHT) <= 1) return;
    window.clearTimeout(scrollTimerRef.current);
    programmaticScrollRef.current = true;
    programmaticTargetRef.current = index * WHEEL_ITEM_HEIGHT;
    onChange(option.value);
    scrollToIndex(index, behavior);
    scrollTimerRef.current = window.setTimeout(() => {
      scrollToIndex(index, "auto");
      programmaticScrollRef.current = false;
      programmaticTargetRef.current = null;
    }, 1200);
  };

  const handleScroll = (event) => {
    if (programmaticScrollRef.current) {
      if (Math.abs(event.currentTarget.scrollTop - programmaticTargetRef.current) <= 1) {
        window.clearTimeout(scrollTimerRef.current);
        programmaticScrollRef.current = false;
        programmaticTargetRef.current = null;
      }
      return;
    }
    const index = Math.max(0, Math.min(options.length - 1, Math.round(event.currentTarget.scrollTop / WHEEL_ITEM_HEIGHT)));
    const option = options[index];
    if (option && String(option.value) !== String(value)) {
      manualScrollRef.current = true;
      onChange(option.value);
    }
  };

  const handleKeyDown = (event) => {
    let nextIndex = selectedIndex;
    if (event.key === "ArrowDown") nextIndex += 1;
    else if (event.key === "ArrowUp") nextIndex -= 1;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = options.length - 1;
    else return;
    event.preventDefault();
    selectIndex(nextIndex);
  };

  return (
    <div className="min-w-0 text-center">
      <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-400">{label}</span>
      <div className="birthdate-wheel-frame relative overflow-hidden rounded-xl">
        <div className="birthdate-wheel-highlight pointer-events-none absolute inset-x-0 top-1/2 z-0 h-[42px] -translate-y-1/2 rounded-sm" aria-hidden="true" />
        <div
          ref={setRefs}
          role="listbox"
          tabIndex={0}
          aria-label={ariaLabel}
          aria-activedescendant={`birthdate-${label.toLowerCase()}-${value}`}
          className="birthdate-wheel relative z-10 h-[210px] overflow-y-auto outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-neutral-400"
          onScroll={handleScroll}
          onKeyDown={handleKeyDown}
        >
          {options.map((option, index) => {
            const selected = String(option.value) === String(value);
            return (
              <button
                id={`birthdate-${label.toLowerCase()}-${option.key ?? option.value}`}
                key={option.key ?? option.value}
                type="button"
                role="option"
                aria-selected={selected}
                tabIndex={-1}
                onClick={() => selectIndex(index)}
                className={`birthdate-wheel-option block w-full snap-center px-1 text-center text-sm transition-colors ${selected ? "is-selected" : ""}`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function formatBirthDateLabel(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return "Select birth date";

  return new Intl.DateTimeFormat("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function dateParts(date) {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

function parseISODate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;

  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (date.getFullYear() !== Number(match[1]) || date.getMonth() !== Number(match[2]) - 1 || date.getDate() !== Number(match[3])) return null;
  return date;
}

function parseSelection(value, fallback, minDate, maxDate) {
  const parsedDate = parseISODate(value) || fallback;
  const boundedDate = parsedDate < minDate ? minDate : parsedDate > maxDate ? maxDate : parsedDate;
  return dateParts(boundedDate);
}

function normalizeSelection(selection) {
  const year = Number(selection.year);
  const month = Number(selection.month);
  const daysInMonth = new Date(year, month, 0).getDate();

  return {
    year,
    month,
    day: Math.min(daysInMonth, Number(selection.day)),
  };
}

export default function BirthDatePickerModal({
  open,
  value,
  onClose,
  onConfirm,
  minDate: minDateValue = "1900-01-01",
  maxDate: maxDateValue,
  defaultValue,
  title = "SET BIRTHDATE",
  submitLabel = "SUBMIT",
  wheelLabel = "Birth",
  closeLabel = "Close birthdate picker",
}) {
  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(() => parseISODate(minDateValue) || new Date(1900, 0, 1), [minDateValue]);
  const maxDate = useMemo(() => parseISODate(maxDateValue) || today, [maxDateValue, today]);
  const fallbackDate = useMemo(() => {
    const requestedDefault = parseISODate(defaultValue);
    if (requestedDefault) return requestedDefault;
    return new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
  }, [defaultValue, today]);
  const [selection, setSelection] = useState(() => parseSelection(value, fallbackDate, minDate, maxDate));
  const [rangeError, setRangeError] = useState("");
  const panelRef = useRef(null);
  const yearSelectRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    setSelection(parseSelection(value, fallbackDate, minDate, maxDate));
    setRangeError("");
    const previousOverflow = document.body.style.overflow;
    const previousActiveElement = document.activeElement;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== "Tab") return;
      const focusable = panelRef.current?.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const focusFrame = window.requestAnimationFrame(() => yearSelectRef.current?.focus());

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActiveElement?.focus?.();
    };
  }, [fallbackDate, maxDate, minDate, open, value]);

  if (!open) return null;

  const firstYear = Math.max(100, minDate.getFullYear() - 10);
  const lastYear = Math.min(9999, maxDate.getFullYear() + 10);
  const years = Array.from({ length: lastYear - firstYear + 1 }, (_, index) => firstYear + index);
  const months = Array.from({ length: 12 }, (_, index) => index + 1);
  const daysInMonth = new Date(selection.year, selection.month, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, index) => index + 1);
  const previousMonthDays = new Date(selection.year, selection.month - 1, 0).getDate();
  const dayOptions = [
    ...[previousMonthDays - 1, previousMonthDays].map((day) => ({ value: `previous-${day}`, label: padDatePart(day) })),
    ...days.map((day) => ({ value: day, label: padDatePart(day) })),
    ...[1, 2].map((day) => ({ value: `next-${day}`, label: padDatePart(day) })),
  ];

  const updateSelection = (part, nextValue) => {
    setRangeError("");
    setSelection((current) => normalizeSelection({ ...current, [part]: nextValue }));
  };

  const updateDay = (nextValue) => {
    setRangeError("");
    if (typeof nextValue === "string" && nextValue.startsWith("previous-")) {
      setSelection((current) => dateParts(new Date(current.year, current.month - 2, Number(nextValue.slice(9)))));
    } else if (typeof nextValue === "string" && nextValue.startsWith("next-")) {
      setSelection((current) => dateParts(new Date(current.year, current.month, Number(nextValue.slice(5)))));
    } else {
      updateSelection("day", nextValue);
    }
  };

  const submitSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const selectedDate = new Date(selection.year, selection.month - 1, selection.day);
    if (selectedDate < minDate || selectedDate > maxDate) {
      setRangeError(`Choose a date from ${formatBirthDateLabel(`${minDate.getFullYear()}-${padDatePart(minDate.getMonth() + 1)}-${padDatePart(minDate.getDate())}`)} to ${formatBirthDateLabel(`${maxDate.getFullYear()}-${padDatePart(maxDate.getMonth() + 1)}-${padDatePart(maxDate.getDate())}`)}.`);
      return;
    }
    onConfirm(
      `${selection.year}-${padDatePart(selection.month)}-${padDatePart(selection.day)}`
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 px-4 py-8 backdrop-blur-sm"
      role="presentation"
      // Opened from inside a modal Dialog, which turns pointer events off outside itself (APP-HR-076).
      style={{ pointerEvents: "auto" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="birthdate-picker-title"
        className="w-full max-w-md overflow-hidden rounded-3xl border border-neutral-200 bg-white text-neutral-900 shadow-2xl dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-50"
      >
        <header className="grid grid-cols-[2.5rem_1fr_2.5rem] items-center border-b border-neutral-200 px-4 py-4 dark:border-neutral-800">
          <span aria-hidden="true" />
          <h2 id="birthdate-picker-title" className="text-center text-sm font-medium tracking-wide">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-neutral-400 transition hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
            aria-label={closeLabel}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <form onSubmit={submitSelection} className="p-5">
          <div className="relative grid grid-cols-3 gap-2">
            <WheelColumn
              label="Year"
              ariaLabel={`${wheelLabel} year`}
              options={years.map((year) => ({ value: year, label: year }))}
              value={selection.year}
              onChange={(nextValue) => updateSelection("year", nextValue)}
              columnRef={yearSelectRef}
            />
            <WheelColumn
              label="Month"
              ariaLabel={`${wheelLabel} month`}
              options={months.map((month) => ({ value: month, label: MONTHS[month - 1].slice(0, 3) }))}
              value={selection.month}
              onChange={(nextValue) => updateSelection("month", nextValue)}
            />
            <WheelColumn
              key={`${selection.year}-${selection.month}`}
              label="Day"
              ariaLabel={`${wheelLabel} day`}
              options={dayOptions}
              value={selection.day}
              onChange={updateDay}
            />
          </div>

          {rangeError && <p role="alert" className="mt-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">{rangeError}</p>}

          <button
            type="submit"
            className="mt-5 w-full rounded-xl bg-neutral-900 py-3.5 text-xs font-semibold tracking-[0.2em] text-white transition hover:bg-neutral-800 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-500 focus-visible:ring-offset-2 dark:bg-neutral-50 dark:text-neutral-900 dark:hover:bg-neutral-200 dark:focus-visible:ring-offset-neutral-900"
          >
            {submitLabel}
          </button>
        </form>
      </section>
    </div>,
    document.body
  );
}
